import React, { useEffect, useMemo, useState } from "react";
import "./PaymentEntry.scss";
import { useNavigate, useParams } from "react-router-dom";
import { adminRequest } from "../../../utils/requestMethods";
import { toast, ToastContainer } from "react-toastify";
import logo from "../../../assets/logo_prod.png";
import { MdArrowBack } from "react-icons/md";

const PaymentEntry = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    invoice_uuid: id,

    amount: "",

    payment_method: "CASH",

    transaction_id: "",

    remarks: "",
  });

  const fetchInvoice = async () => {
    try {
      const response = await adminRequest.get(`/invoice/view/${id}`);

      setInvoice(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, []);

  const remainingDue = useMemo(() => {
    if (!invoice) return 0;

    return Number(invoice.due_amount);
  }, [invoice]);

  const handleChange = (e) => {
    setFormData({
      ...formData,

      [e.target.name]: e.target.value,
    });
  };

  const handleFullPayment = () => {
    setFormData({
      ...formData,

      amount: remainingDue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Number(formData.amount) <= 0) {
      toast.error("Payment amount must be greater than zero");

      return;
    }

    if (Number(formData.amount) > remainingDue) {
      toast.error("Payment amount exceeds due amount");

      return;
    }

    setIsSubmitting(true);

    try {
      const response = await toast.promise(
        adminRequest.post("/invoice/payment/create", formData),
        {
          pending: "Recording payment...",
        },
      );

      toast.success(response.data.message);

      setTimeout(() => {
        navigate(`/studentFees/payment/receipt/${id}`);
      }, 1200);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!invoice) {
    return <div className="paymentEntryContainer">Loading...</div>;
  }

  return (
    <div className="paymentEntryContainer">
      <div className="pageHeader">
        <div className="leftSection">
          <button className="backBtn" onClick={() => navigate(-1)}>
            <MdArrowBack />
          </button>

          <h2>Payment Entry</h2>
        </div>
      </div>

      <div className="paymentLayout">
        <div className="invoiceReceipt">
          <div className="topSerial">
            <p>
              Serial No:
              <span>{invoice.invoice_number}</span>
            </p>
          </div>

          <div className="receiptHeader">
            <div className="logoSection">
              <img src={logo} alt="college-logo" />
            </div>

            <div className="collegeDetails">
              <h2>Kantipur College of Management and Information Technology</h2>

              <p>Baneshwor, Kathmandu, Nepal</p>

              <p>Tel: 4479939</p>
            </div>
          </div>

          <div className="billTitle">
            <h3>Bill</h3>
          </div>

          <div className="invoiceMeta">
            <div className="metaRow">
              <div className="metaItem">
                <span>Name:</span>

                <p>{invoice.Student?.fullName}</p>
              </div>

              <div className="metaItem">
                <span>Semester:</span>

                <p>{invoice.semester}</p>
              </div>

              <div className="metaItem date">
                <span>Date:</span>

                <p>{invoice.invoice_date}</p>
              </div>
            </div>
          </div>

          <div className="invoiceTable">
            <table>
              <thead>
                <tr>
                  <th>S.N</th>
                  <th>DESCRIPTION</th>
                  <th>Amount</th>
                  <th>Remark</th>
                </tr>
              </thead>

              <tbody>
                {invoice.FeeInvoiceItems?.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>

                    <td>{item.title}</td>

                    <td>Rs. {item.amount}</td>

                    <td>{item.description}</td>
                  </tr>
                ))}

                {invoice.discount_amount > 0 && (
                  <tr>
                    <td>{invoice.FeeInvoiceItems?.length + 1}</td>

                    <td>Discount</td>

                    <td>- Rs. {invoice.discount_amount}</td>

                    <td>{invoice.discount_reason}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="totalSection">
            <p>
              Total:
              <span>Rs. {invoice.payable_amount}</span>
            </p>
          </div>

          <div className="bottomSection">
            <div className="receivedBy">
              <p>Received By</p>
            </div>

            <div className="officeSeal">
              <p>Office Seal</p>
            </div>
          </div>
        </div>
        <form className="paymentFormCard" onSubmit={handleSubmit}>
          <div className="cardHeader">
            <h3>Record Payment</h3>
          </div>

          <div className="formGroup">
            <label>Payment Amount</label>

            <div className="amountInputWrapper">
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                required
              />

              <button
                type="button"
                className="fullPayBtn"
                onClick={handleFullPayment}
              >
                Full Pay
              </button>
            </div>
          </div>

          <div className="formGroup">
            <label>Payment Method</label>

            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
            >
              <option value="CASH">CASH</option>

              <option value="BANK">BANK</option>

              <option value="ONLINE">ONLINE</option>

              <option value="CHEQUE">CHEQUE</option>
            </select>
          </div>

          {formData.payment_method !== "CASH" && (
            <div className="formGroup">
              <label>Transaction ID</label>

              <input
                type="text"
                name="transaction_id"
                value={formData.transaction_id}
                onChange={handleChange}
                placeholder="Enter transaction id"
              />
            </div>
          )}

          <div className="formGroup">
            <label>Remarks</label>

            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              placeholder="Optional remarks"
            />
          </div>

          <div className="paymentInfoBox">
            <div className="infoItem">
              <span>Remaining Due</span>

              <p>Rs. {remainingDue}</p>
            </div>

            <div className="infoItem">
              <span>Paying Amount</span>

              <p>Rs. {formData.amount || 0}</p>
            </div>
          </div>

          <div className="submitSection">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Processing..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
};

export default PaymentEntry;
